import React from 'react'

import styled, { keyframes } from 'styled-components'

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const pulse = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`

const StyledSVG = styled.svg<{ size: string; stroke?: string }>`
  animation: 1.5s ${rotate} linear infinite;
  height: ${({ size }) => size};
  width: ${({ size }) => size};
  path {
    stroke: ${({ stroke, theme }) => stroke ?? theme.primary1};
    stroke-linecap: round;
  }
`

// Skeleton loading components
export const SkeletonWrapper = styled.div<{ width?: string; height?: string; borderRadius?: string }>`
  background: ${({ theme }) => theme.bg3};
  border-radius: ${({ borderRadius, theme }) => borderRadius || theme.borderRadius.small};
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || '16px'};
  animation: ${pulse} 2s ease-in-out infinite;
`

export const SkeletonText = styled(SkeletonWrapper)`
  height: ${({ theme }) => theme.fontSize.lg};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  width: ${({ width }) => width || '60%'};
`

export const SkeletonTitle = styled(SkeletonWrapper)`
  height: ${({ theme }) => theme.fontSize.xxxl};
  width: 70%;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

export const SkeletonButton = styled(SkeletonWrapper)`
  height: 48px;
  width: 120px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
`

export const SkeletonAvatar = styled(SkeletonWrapper)`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.round};
`

export const SkeletonInput = styled(SkeletonWrapper)`
  height: 56px;
  border-radius: ${({ theme }) => theme.borderRadius.large};
`

export const SkeletonCard = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.bg2};
  border: 1px solid ${({ theme }) => theme.bg3};

  ${SkeletonText} {
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }

  ${SkeletonText}:last-child {
    width: 40%;
    margin-bottom: 0;
  }
`

export const SkeletonTable = styled.div`
  ${SkeletonText} {
    height: ${({ theme }) => theme.fontSize.md};
    margin-bottom: ${({ theme }) => theme.spacing.sm};

    &:nth-child(odd) {
      width: 70%;
    }

    &:nth-child(even) {
      width: 50%;
    }
  }
`

// Empty state components
export const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxxl};
  text-align: center;
  min-height: 200px;
`

export const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  opacity: 0.6;
`

export const EmptyStateTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.xxl};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.text1};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
`

export const EmptyStateDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  color: ${({ theme }) => theme.text2};
  margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
  max-width: 400px;
  line-height: 1.5;
`

export const EmptyStateAction = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  justify-content: center;
`

/**
 * Takes in custom size and stroke for circle color, default to primary color as fill,
 * need ...rest for layered styles on top
 */
export default function Loader({
  size = '24px',
  stroke,
  ...rest
}: {
  size?: string
  stroke?: string
  [k: string]: any
}) {
  return (
    <StyledSVG viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" size={size} stroke={stroke} {...rest}>
      <path
        d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 9.27455 20.9097 6.80375 19.1414 5"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </StyledSVG>
  )
}
